import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { KeyRound, Plus, FileText, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const LeaseProductsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '', short_name: '', description: '', asset_type: 'equipment', lease_type: 'finance',
    min_lease_amount: '', max_lease_amount: '', interest_rate: '', interest_method: 'declining_balance',
    min_term_months: '12', max_term_months: '60', residual_value_percent: '0',
    deposit_required_percent: '10', early_termination_fee_percent: '5',
    insurance_required: true, accounting_treatment: 'ifrs16', min_credit_score: '0', category: 'equipment',
  });
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [contractFile, setContractFile] = useState(null);
  const contractInputRef = React.useRef(null);
  const { user } = useAuth();

  useEffect(() => { load(); loadPending(); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/lease-products'); setItems(res.data); }
    catch { setItems([]); }
    setLoading(false);
  };

  const loadPending = async () => {
    try {
      const res = await api.get('/pending-product-changes');
      setPendingChanges(res.data.filter(c => c.product_type === 'lease_product'));
    } catch { /* ignore */ }
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Product name is required'); return; }
    try {
      await api.post('/lease-products', {
        ...form,
        min_lease_amount: parseFloat(form.min_lease_amount) || 0,
        max_lease_amount: parseFloat(form.max_lease_amount) || 0,
        interest_rate: parseFloat(form.interest_rate) || 0,
        min_term_months: parseInt(form.min_term_months) || 12,
        max_term_months: parseInt(form.max_term_months) || 60,
        residual_value_percent: parseFloat(form.residual_value_percent) || 0,
        deposit_required_percent: parseFloat(form.deposit_required_percent) || 10,
        early_termination_fee_percent: parseFloat(form.early_termination_fee_percent) || 5,
        min_credit_score: parseInt(form.min_credit_score) || 0,
        covenants: {
          dscr: form.covenant_dscr ? parseFloat(form.covenant_dscr) : null,
          ltv: form.covenant_ltv ? parseFloat(form.covenant_ltv) : null,
        },
      });
      toast.success('Lease product created');
      setShowModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const openEditModal = (p) => {
    setEditProduct(p);
    setEditForm({
      name: p.name || '', short_name: p.short_name || '', description: p.description || '',
      asset_type: p.asset_type || 'equipment', lease_type: p.lease_type || 'finance',
      interest_rate: p.interest_rate || '', interest_method: p.interest_method || 'declining_balance',
      min_term_months: p.min_term_months || '', max_term_months: p.max_term_months || '',
      residual_value_percent: p.residual_value_percent || '', deposit_required_percent: p.deposit_required_percent || '',
      early_termination_fee_percent: p.early_termination_fee_percent || '',
      accounting_treatment: p.accounting_treatment || 'ifrs16',
      min_credit_score: p.min_credit_score || '',
      effective_date: new Date().toISOString().slice(0, 10),
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      const payload = {
        ...editForm,
        interest_rate: parseFloat(editForm.interest_rate) || undefined,
        min_term_months: parseInt(editForm.min_term_months) || undefined,
        max_term_months: parseInt(editForm.max_term_months) || undefined,
        residual_value_percent: parseFloat(editForm.residual_value_percent) || 0,
        deposit_required_percent: parseFloat(editForm.deposit_required_percent) || 0,
        early_termination_fee_percent: parseFloat(editForm.early_termination_fee_percent) || 0,
        min_credit_score: parseInt(editForm.min_credit_score) || 0,
      };
      const res = await api.put(`/lease-products/${editProduct.id}`, payload);
      toast.success(res.data.status === 'pending' ? 'Change submitted for checker approval' : 'Product updated');
      setShowEditModal(false); load(); loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleApprove = async (changeId) => {
    try { await api.post(`/pending-product-changes/${changeId}/approve`, { comments: '' }); toast.success('Approved'); loadPending(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleReject = async (changeId) => {
    try { await api.post(`/pending-product-changes/${changeId}/reject`, { comments: '' }); toast.success('Rejected'); loadPending(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleContractUpload = async (productId) => {
    if (!contractFile) return;
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const b64 = e.target.result.split(',')[1];
        await api.post(`/lease-products/${productId}/contract/upload`, { file_data: b64, file_name: contractFile.name, file_type: contractFile.type });
        toast.success('Contract uploaded'); setContractFile(null); load();
      };
      reader.readAsDataURL(contractFile);
    } catch { toast.error('Failed to upload contract'); }
  };

  const leaseTypeLabels = { finance: 'Finance Lease', operating: 'Operating Lease' };
  const assetLabels = { equipment: 'Equipment', vehicle: 'Vehicle', property: 'Property', machinery: 'Machinery', technology: 'Technology', other: 'Other' };

  return (
    <div className="space-y-6 fade-in" data-testid="lease-products-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>Products</span><span>/</span><span className="text-[#1f1f1f] font-medium">Lease Products</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">PRODUCT CONFIGURATION</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Lease Products</h1>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-lease-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Lease Product
        </Button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="lease-products-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Product</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Asset</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Rate</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Term</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Accounting</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-gray-400"><KeyRound className="w-8 h-8 mx-auto mb-2 text-gray-300" />No lease products.</td></tr> :
            items.map(item => (
              <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`lease-row-${item.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{item.name}</div><div className="text-xs text-gray-400">{item.short_name}</div></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${item.lease_type === 'finance' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>{leaseTypeLabels[item.lease_type] || item.lease_type}</span></td>
                <td className="px-4 py-3 text-sm text-gray-600">{assetLabels[item.asset_type] || item.asset_type}</td>
                <td className="px-4 py-3 text-sm text-right font-semibold text-[#C5A059]">{item.interest_rate || 0}%</td>
                <td className="px-4 py-3 text-sm text-gray-600">{item.min_term_months || 0} - {item.max_term_months || 0}m</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">{item.accounting_treatment === 'ifrs16' ? 'IFRS 16' : 'IAS 17'}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${item.is_active !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{item.is_active !== false ? 'Active' : 'Inactive'}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-gray-500 hover:text-[#1f1f1f]" onClick={() => openEditModal(item)} data-testid={`lease-edit-${item.id}`}>Edit</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="lease-create-desc">
          <DialogHeader><DialogTitle>Create Lease Product</DialogTitle></DialogHeader>
          <p id="lease-create-desc" className="sr-only">Form</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Product Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="lease-name-input" /></div>
              <div><Label>Short Name</Label><Input value={form.short_name} onChange={e => setForm({...form, short_name: e.target.value})} /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Lease Type *</Label>
                <Select value={form.lease_type} onValueChange={v => setForm({...form, lease_type: v})}>
                  <SelectTrigger data-testid="lease-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="finance">Finance Lease</SelectItem><SelectItem value="operating">Operating Lease</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Asset Category *</Label>
                <Select value={form.asset_type} onValueChange={v => setForm({...form, asset_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equipment">Equipment</SelectItem><SelectItem value="vehicle">Vehicle</SelectItem>
                    <SelectItem value="property">Property</SelectItem><SelectItem value="machinery">Machinery</SelectItem>
                    <SelectItem value="technology">Technology</SelectItem><SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Accounting</Label>
                <Select value={form.accounting_treatment} onValueChange={v => setForm({...form, accounting_treatment: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ifrs16">IFRS 16</SelectItem><SelectItem value="ias17">IAS 17 (Legacy)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Interest Rate (%)</Label><Input type="number" step="0.01" value={form.interest_rate} onChange={e => setForm({...form, interest_rate: e.target.value})} data-testid="lease-rate-input" /></div>
              <div><Label>Min Lease Amount</Label><Input type="number" value={form.min_lease_amount} onChange={e => setForm({...form, min_lease_amount: e.target.value})} /></div>
              <div><Label>Max Lease Amount</Label><Input type="number" value={form.max_lease_amount} onChange={e => setForm({...form, max_lease_amount: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Min Term (months)</Label><Input type="number" value={form.min_term_months} onChange={e => setForm({...form, min_term_months: e.target.value})} /></div>
              <div><Label>Max Term (months)</Label><Input type="number" value={form.max_term_months} onChange={e => setForm({...form, max_term_months: e.target.value})} /></div>
              <div><Label>Residual Value (%)</Label><Input type="number" step="0.1" value={form.residual_value_percent} onChange={e => setForm({...form, residual_value_percent: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Deposit Required (%)</Label><Input type="number" value={form.deposit_required_percent} onChange={e => setForm({...form, deposit_required_percent: e.target.value})} /></div>
              <div><Label>Early Termination Fee (%)</Label><Input type="number" value={form.early_termination_fee_percent} onChange={e => setForm({...form, early_termination_fee_percent: e.target.value})} /></div>
              <div><Label>Min Credit Score</Label><Input type="number" value={form.min_credit_score} onChange={e => setForm({...form, min_credit_score: e.target.value})} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.insurance_required} onCheckedChange={v => setForm({...form, insurance_required: v})} />
              <Label>Insurance Required</Label>
            </div>
            {/* Lease Covenants */}
            <div className="p-4 border border-gray-200 bg-gray-50 space-y-3" data-testid="lease-covenants-section">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Lease Covenants</Label>
              <p className="text-xs text-gray-400">Define covenant thresholds. Leave blank to disable.</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs">DSCR Minimum</Label><Input type="number" step="0.05" value={form.covenant_dscr || ''} onChange={e => setForm({...form, covenant_dscr: e.target.value})} placeholder="1.25" data-testid="lease-cov-dscr" /></div>
                <div><Label className="text-xs">LTV Maximum %</Label><Input type="number" step="1" value={form.covenant_ltv || ''} onChange={e => setForm({...form, covenant_ltv: e.target.value})} placeholder="80" data-testid="lease-cov-ltv" /></div>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="lease-submit-btn">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="lease-edit-desc">
          <DialogHeader><DialogTitle>Edit: {editProduct?.name}</DialogTitle></DialogHeader>
          <p id="lease-edit-desc" className="sr-only">Edit form</p>
          {editProduct && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800">
                {user?.role === 'admin' ? 'Admin: changes apply immediately.' : 'Changes will be submitted for checker approval.'}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>
                <div><Label>Short Name</Label><Input value={editForm.short_name} onChange={e => setEditForm({...editForm, short_name: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Lease Type</Label>
                  <Select value={editForm.lease_type} onValueChange={v => setEditForm({...editForm, lease_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="finance">Finance Lease</SelectItem><SelectItem value="operating">Operating Lease</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Accounting</Label>
                  <Select value={editForm.accounting_treatment} onValueChange={v => setEditForm({...editForm, accounting_treatment: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="ifrs16">IFRS 16</SelectItem><SelectItem value="ias17">IAS 17</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Effective Date</Label><Input type="date" value={editForm.effective_date} onChange={e => setEditForm({...editForm, effective_date: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Rate (%)</Label><Input type="number" step="0.01" value={editForm.interest_rate} onChange={e => setEditForm({...editForm, interest_rate: e.target.value})} /></div>
                <div><Label>Residual (%)</Label><Input type="number" value={editForm.residual_value_percent} onChange={e => setEditForm({...editForm, residual_value_percent: e.target.value})} /></div>
                <div><Label>Deposit (%)</Label><Input type="number" value={editForm.deposit_required_percent} onChange={e => setEditForm({...editForm, deposit_required_percent: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Min Term</Label><Input type="number" value={editForm.min_term_months} onChange={e => setEditForm({...editForm, min_term_months: e.target.value})} /></div>
                <div><Label>Max Term</Label><Input type="number" value={editForm.max_term_months} onChange={e => setEditForm({...editForm, max_term_months: e.target.value})} /></div>
                <div><Label>Early Term Fee (%)</Label><Input type="number" value={editForm.early_termination_fee_percent} onChange={e => setEditForm({...editForm, early_termination_fee_percent: e.target.value})} /></div>
              </div>
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <div className="p-3 border border-gray-200 bg-gray-50 space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">T&C / Contract Document</Label>
                  {editProduct.contract_file_name && (
                    <div className="flex items-center gap-2 text-xs text-gray-600"><FileText className="w-3.5 h-3.5" /><span>Current: {editProduct.contract_file_name} (v{editProduct.contract_version})</span></div>
                  )}
                  <div className="flex gap-2 items-center">
                    <Input type="file" accept=".pdf,.doc,.docx" ref={contractInputRef} onChange={e => setContractFile(e.target.files?.[0])} className="text-xs" />
                    <Button size="sm" disabled={!contractFile} onClick={() => handleContractUpload(editProduct.id)} className="text-xs bg-[#1f1f1f] text-white" data-testid="lease-upload-contract-btn">Upload</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button><Button onClick={handleEditSubmit} className="bg-[#1f1f1f] text-white" data-testid="lease-edit-submit">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Changes */}
      {pendingChanges.length > 0 && (user?.role === 'admin' || user?.role === 'manager') && (
        <div className="bg-white border border-amber-200 p-4" data-testid="lease-pending-changes-section">
          <h3 className="text-sm font-bold text-[#1f1f1f] mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" />Pending Lease Product Changes ({pendingChanges.length})</h3>
          <div className="space-y-2">
            {pendingChanges.map(pc => (
              <div key={pc.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-100">
                <div><p className="text-sm font-medium">{pc.product_name}</p><p className="text-xs text-gray-500">By: {pc.maker_name} ({pc.maker_role})</p></div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleApprove(pc.id)} className="bg-green-600 text-white text-xs">Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => handleReject(pc.id)} className="text-xs text-red-600 border-red-200">Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
export default LeaseProductsPage;
